/**
 * F34 (Wave 2D) — admin trainer package-consumption API client.
 *
 * The single F34 admin touch: reverse a trainer package-consumption decrement
 * (incident response — e.g. a no-show was wrongly recorded against a client,
 * burning a session). Reversing credits the session back.
 *
 *   POST /admin/trainer-package-consumptions/:id/reverse
 *     body: ReverseTrainerConsumptionInput { reason }   (min 1 / max 500)
 *     200 → { id, purchaseId, reversedAt, reversalReason }
 *     404 NOT_FOUND
 *     409 CONSUMPTION_ALREADY_REVERSED
 *     422 VALIDATION_ERROR
 *
 * There is no GET-consumption-by-id endpoint at F34, so the admin surface is a
 * lookup-by-id + reverse worklist (see /trainer-package-consumptions page). The
 * server gates the route on `trainer_package_consumption:reverse` (super_admin +
 * support) and audits the action via the F06 onSend hook.
 */
import type { ReverseTrainerConsumptionInput } from '@bravemove/types';
import { adminFetch } from '../auth/apiClient';

/** Server response from POST .../:id/reverse. */
export interface ReverseConsumptionResult {
  readonly id: string;
  readonly purchaseId: string;
  readonly reversedAt: string;
  readonly reversalReason: string;
}

interface DataEnvelope<T> {
  readonly data: T;
}

export const adminTrainerConsumptionsApi = {
  /**
   * POST /admin/trainer-package-consumptions/:id/reverse — credit a
   * completed/no-show decrement back to the client's package balance.
   *
   * Permission: `trainer_package_consumption:reverse` (server-enforced).
   * Throws `ApiError` (carrying `.status` / `.code`) on 404 / 409 / 422.
   */
  reverse: async (
    consumptionId: string,
    input: ReverseTrainerConsumptionInput,
  ): Promise<ReverseConsumptionResult> => {
    const body = await adminFetch<DataEnvelope<ReverseConsumptionResult>>(
      `/admin/trainer-package-consumptions/${encodeURIComponent(consumptionId)}/reverse`,
      {
        method: 'POST',
        body: input as unknown as Record<string, unknown>,
      },
    );
    return body.data;
  },
};
